import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import I18n from '@/modules/i18n';
import * as Routes from '@/modules/routes';
import {
  AI_GRADE_BANDS,
  GRADE_BAND_LABELS,
  type ReadingGradeLevels,
} from '@/components/shared/TextControlsToolbar/AIReadingLevelButton';
import cn from '@/utils/cn';
import AIBadge from '@/components/shared/AIBadge';
import { TCIDropdown } from '@/components/ui/Dropdown';
import Icon from '@/components/ui/Icon';
import { TCITooltip } from '@/components/ui/Tooltip';
import { COLORS } from '@/components/ui/ui.constants';
import { formatLocalTimestampWithFormat } from '@/components/common/Utils';
import useStudentSeatSyncStore from '@/stores/useStudentSeatSyncStore';

interface ReadingLevelSelectProps {
  aiReadingLevelEnabled?: boolean;
  /** AI grade bands this program may offer (Program#available_ai_reading_levels). */
  availableAiGradeBands?: string[];
  classroomId?: string;
  readingLevel: ReadingGradeLevels;
  studentSeatId?: string;
  updatedAt?: string;
}

const getDisplayLabel = (level: ReadingGradeLevels): string => {
  if (level === null) return 'Off';
  if (level === 'b') return I18n.t('reading_level_b');
  if (AI_GRADE_BANDS.includes(level as any)) {
    return GRADE_BAND_LABELS[level as keyof typeof GRADE_BAND_LABELS];
  }
  return 'Off';
};

const CLASSROOM_READING_LEVEL_CHANGED_EVENT = 'classroomReadingLevelChanged';

const ReadingLevelSelect = ({
  aiReadingLevelEnabled, availableAiGradeBands = [], classroomId, readingLevel, studentSeatId, updatedAt,
}: ReadingLevelSelectProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<ReadingGradeLevels>(readingLevel);
  const lastUpdate = useStudentSeatSyncStore(state => state.lastUpdate);
  const notify = useStudentSeatSyncStore(state => state.notify);

  const isSelectedAiBand = selectedLevel !== null
    && selectedLevel !== 'b'
    && AI_GRADE_BANDS.includes(selectedLevel as any);
  const isSubscriberDisabled = !aiReadingLevelEnabled && isSelectedAiBand;
  const isBandUnavailable = aiReadingLevelEnabled
    && isSelectedAiBand
    && !availableAiGradeBands.includes(selectedLevel as string);
  const isOrphaned = isSubscriberDisabled || isBandUnavailable;

  const getTimeContainer = () => document.getElementById('updated-time-container');

  const updateTime = (dateTime) => {
    const timeContainer = getTimeContainer();

    if (!timeContainer) return;
    timeContainer.innerHTML = `Autosaved on: ${formatLocalTimestampWithFormat(dateTime, 'MMMM DD, YYYY hh:mm A')}`;
  };

  // Listen for classroom-level reading level changes (student seats only)
  useEffect(() => {
    if (!studentSeatId || !classroomId) return;

    const handleClassroomLevelChange = (event: CustomEvent) => {
      if (event.detail.classroomId === classroomId) {
        setSelectedLevel(event.detail.readingLevel);
      }
    };

    window.addEventListener(
      CLASSROOM_READING_LEVEL_CHANGED_EVENT,
      handleClassroomLevelChange as EventListener,
    );

    return () => {
      window.removeEventListener(
        CLASSROOM_READING_LEVEL_CHANGED_EVENT,
        handleClassroomLevelChange as EventListener,
      );
    };
  }, [studentSeatId, classroomId]);

  // Sync when the Standard toggle auto-corrects the reading level
  useEffect(() => {
    if (!studentSeatId || !lastUpdate) return;
    if (lastUpdate.studentSeatId !== String(studentSeatId)) return;

    setSelectedLevel(lastUpdate.readingLevel as ReadingGradeLevels ?? null);
  }, [lastUpdate, studentSeatId]);

  useEffect(() => {
    if (!updatedAt) return;
    updateTime(updatedAt);
  }, [updatedAt]);

  const { isLoading, mutateAsync } = useMutation({
    mutationFn: (level: ReadingGradeLevels) => {
      if (studentSeatId) {
        return axios.patch(
          Routes.plato_api_student_seat_path(studentSeatId),
          { student_seat: { reading_level: level } },
        );
      }
      if (classroomId) {
        return axios.patch(
          Routes.plato_api_classroom_path(classroomId),
          { classroom: { reading_level: level } },
        );
      }
      return Promise.reject(new Error('No classroomId or studentSeatId provided'));
    },
    mutationKey: studentSeatId
      ? ['updateStudentSeatReadingLevel', studentSeatId]
      : ['updateClassroomReadingLevel', classroomId],
  });

  const handleSelect = async (level: ReadingGradeLevels) => {
    setDropdownOpen(false);

    if (!classroomId && !studentSeatId) return;

    const previousLevel = selectedLevel;
    setSelectedLevel(level);

    try {
      const result = await mutateAsync(level);

      // Set updated at time in chosen html element.
      const updatedAtTime = result.data?.data?.time_updated_at || result.data?.data?.updated_at;

      updateTime(updatedAtTime);

      // If this is a classroom-level update, broadcast the change to all student selects
      if (classroomId && !studentSeatId) {
        window.dispatchEvent(new CustomEvent(CLASSROOM_READING_LEVEL_CHANGED_EVENT, {
          detail: { classroomId, readingLevel: level },
        }));
      }

      // Notify the Standard toggle if the backend auto-corrected reading_level_a_enabled
      if (studentSeatId && result.data?.data?.reading_level_a_enabled !== undefined) {
        notify({
          readingLevel: result.data.data.reading_level,
          readingLevelAEnabled: result.data.data.reading_level_a_enabled,
          studentSeatId: String(studentSeatId),
        });
      }
    } catch (error) {
      console.error('Error updating reading level:', error);
      setSelectedLevel(previousLevel);
    }
  };

  const isSelected = (value: ReadingGradeLevels) => value === selectedLevel;

  const orphanedTooltipText = isSubscriberDisabled
    ? I18n.t('ai_reading_level_disabled_tooltip')
    : I18n.t('ai_reading_level_band_unavailable_tooltip');

  return (
    <span className="tw-flex tw-items-center tw-gap-1">
      <TCIDropdown open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <TCIDropdown.Trigger asChild>
          <button
            className={cn(
              'tw-flex tw-items-center tw-justify-between tw-gap-2',
              'tw-h-6 tw-px-2.5 tw-w-full',
              'tw-bg-white tw-border tw-border-solid tw-border-med-grey tw-rounded',
              'tw-cursor-pointer hover:tw-shadow-md',
              'tw-text-sm',
              'focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-tci-purple focus:tw-border-transparent',
              isLoading && 'tw-opacity-50 tw-cursor-not-allowed',
            )}
            disabled={isLoading}
            type="button"
          >
            <span>{getDisplayLabel(selectedLevel)}</span>
            {isLoading ? (
              <i aria-hidden="true" className="fa fa-spinner fa-spin tw-text-tci-purple" />
            ) : (
              <i aria-hidden="true" className="fa fa-caret-down tw-text-tci-purple" />
            )}
          </button>
        </TCIDropdown.Trigger>
        <TCIDropdown.Content align="start" side="bottom" width="small">
          {/* LEVEL A / STANDARD / OFF */}
          <TCIDropdown.Item className="!tw-px-2" onSelect={() => handleSelect(null)}>
            <span className="tw-flex tw-items-center tw-gap-2">
              {isSelected(null) && (
                <Icon aria-hidden="true" color={COLORS.green} icon="Checkmark" size={16} />
              )}
              {!isSelected(null) && <span className="tw-w-4" />}
              <span>Off</span>
            </span>
          </TCIDropdown.Item>
          {/* LEVEL B */}
          <TCIDropdown.Item className="!tw-px-2" onSelect={() => handleSelect('b')}>
            <span className="tw-flex tw-items-center tw-gap-2">
              {isSelected('b') && (
                <Icon aria-hidden="true" color={COLORS.green} icon="Checkmark" size={16} />
              )}
              {!isSelected('b') && <span className="tw-w-4" />}
              <span>{I18n.t('reading_level_b')}</span>
            </span>
          </TCIDropdown.Item>
          {/* AI Reading Levels */}
          {aiReadingLevelEnabled && AI_GRADE_BANDS.filter(band => availableAiGradeBands.includes(band)).map(band => (
            <TCIDropdown.Item key={band} className="!tw-px-2" onSelect={() => handleSelect(band)}>
              <span className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-w-full">
                {isSelected(band) && (
                  <Icon aria-hidden="true" color={COLORS.green} icon="Checkmark" size={16} />
                )}
                {!isSelected(band) && <span className="tw-w-4" />}
                <span className="tw-w-12">{GRADE_BAND_LABELS[band]}</span>
                <AIBadge />
              </span>
            </TCIDropdown.Item>
          ))}
        </TCIDropdown.Content>
      </TCIDropdown>
      {isOrphaned && (
        <TCITooltip>
          <TCITooltip.Trigger asChild>
            <span className="tw-flex tw-shrink-0">
              <Icon color={COLORS.red} icon="WarningOutlined" size={20} />
            </span>
          </TCITooltip.Trigger>
          <TCITooltip.Content side="right">
            {orphanedTooltipText}
          </TCITooltip.Content>
        </TCITooltip>
      )}
    </span>
  );
};

export default ReadingLevelSelect;
